import { Box, Text, WizardStepRenderParams, useOnNext } from '@factorialco/deprecated-design-system'
import { CreateSoftwaremanagementSoftwareSetupCardholderDataInput } from 'generated/resources/graphql'
import capitalize from 'lodash/capitalize'
import omit from 'lodash/omit'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { WizardData } from 'modules/software_management/components/SoftwareWizard/types'
import { useCreateCustomSoftware } from 'modules/software_management/hooks/useCreateCustomSoftware'
import { useCreateSoftwareSetup } from 'modules/software_management/hooks/useCreateSoftwareSetup'
import { useUpdateEmployee } from 'modules/software_management/hooks/useUpdateEmployee'

export const EmployeesStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { employeeSelection, errors } = data

  const formattedSoftware = capitalize(data.softwareId || data.softwareName)

  const { create: createSoftwareSetup } = useCreateSoftwareSetup()
  const { createCustomSoftware } = useCreateCustomSoftware()
  const { updateEmployee } = useUpdateEmployee()

  const createSoftware = async (data: WizardData) => {
    let { softwareId } = data
    const { softwareName } = data
    const currentInformation = data.employee
    const cardholder = currentInformation?.bankingCardholder
    const cardholderExists = !!cardholder
    let cardholderData: CreateSoftwaremanagementSoftwareSetupCardholderDataInput | undefined
    const createCard = data.spendType === 'card'

    if (softwareName) {
      const response = await createCustomSoftware({
        name: softwareName,
      })

      const result = handleResult(response, (formErrors) => {
        onDataChanged({
          ...data,
          success: false,
          errors: formErrors,
        })
      })

      if (result.success) {
        track(trackingEvents.softwareManagement.createCustomSoftware, {
          name: softwareName,
        })
        softwareId = result.data.customSoftware!.id
      }
    }

    if (
      !cardholderExists &&
      currentInformation?.email &&
      currentInformation?.firstName &&
      currentInformation?.lastName &&
      data.phoneNumber &&
      data.birthDate &&
      data.countryCode
    ) {
      cardholderData = {
        dateOfBirth: data.birthDate,
        email: currentInformation.email,
        firstName: currentInformation.firstName,
        lastName: currentInformation.lastName,
        phoneNumber: data.phoneNumber,
        phoneCountryCode: data.countryCode,
      }
    }

    const response = await createSoftwareSetup({
      softwareId: softwareId!,
      employeeSelection: data.employeeSelection,
      spendingPeriod: data.spendingPeriod!,
      spendingAmount: data.spendingAmount!,
      createCard: createCard,
      ...(createCard && { cardholderData: cardholderData }),
    })

    const handled = handleResult(response, (formErrors) => {
      onDataChanged({
        ...data,
        success: false,
        errors: formErrors,
      })
    })

    if (handled.success) {
      const softwareSetup = handled.data.softwareSetup!
      const { id: software_id, name: software_name } =
        softwareSetup.software || softwareSetup.customSoftware!

      track(trackingEvents.softwareManagement.createSoftwareSetup, {
        software_id,
        software_name,
        software_setup_id: softwareSetup!.id,
        employees_count:
          data.employeeSelection.type === 'manual' ? data.employeeSelection.ids.length : 0,
        with_custom_software: !!softwareSetup!.customSoftware,
      })

      updateEmployee({
        id: data.employeeId,
        phoneNumber: data.phoneNumber!,
      })
      onDataChanged({
        ...data,
        success: true,
        setupId: softwareSetup!.id,
      })
    }
  }

  useEffect(() => {
    track(trackingEvents.softwareManagement.goToSoftwareSetupWizardStep, {
      step: 3,
    })
  }, [])

  useOnNext(createSoftware)

  const renderOtherStepsErrors = () =>
    Object.values(omit(errors, ['employeeIds'])).map((error) => (
      <Box alignItems="flexStart" key={error}>
        <Text key={error} size="200" color="radicalRed1000" capitalize>
          {error}
        </Text>
      </Box>
    ))

  return (
    <Box gap="s24" maxWidth="s600">
      <Box gap="s8">
        <Text.Header2>
          {i18n.tView('software_management.initial_setup.wizard.steps.employees.header', {
            software: <Text.Header2 color="viridian1200">{formattedSoftware}</Text.Header2>,
          })}
        </Text.Header2>

        <Text.Normal>
          {i18n.t('software_management.initial_setup.wizard.steps.employees.description')}
        </Text.Normal>
      </Box>

      <EmployeeSelectorV2
        value={employeeSelection.type === 'manual' ? employeeSelection.ids : undefined}
        multiple
        onChange={(selection) => onDataChanged({ ...data, employeeSelection: selection })}
      />

      {renderOtherStepsErrors()}
    </Box>
  )
}
